import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import OptimizeResults from '../components/OptimizeResults';
import type { OptimizeResponse } from '../types/optimize';

const S: Record<string, React.CSSProperties> = {
  page: { maxWidth: 860, margin: '0 auto', padding: '24px 16px', fontFamily: 'Arial, sans-serif' },
  back: { background: 'none', border: 'none', color: '#2952CC', cursor: 'pointer', fontSize: 14, marginBottom: 16, padding: 0 },
  h1: { color: '#2952CC', fontSize: 22, fontWeight: 700, marginBottom: 4 },
  sub: { color: '#5D6D7E', fontSize: 14, marginBottom: 24 },
  card: { background: 'white', borderRadius: 10, padding: 20, marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.07)' },
  cardTitle: { color: '#2952CC', fontWeight: 700, fontSize: 16, marginBottom: 12 },
  label: { fontSize: 13, fontWeight: 600, color: '#2952CC', marginBottom: 6, display: 'block' },
  select: { padding: '8px 12px', borderRadius: 6, border: '1.5px solid #C5D5FF', fontSize: 14, width: '100%', marginBottom: 12 },
  checkGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 },
  checkItem: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', padding: '6px 8px', borderRadius: 6, border: '1px solid #E8EEF2', background: '#F8FBFD' },
  checkItemOn: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', padding: '6px 8px', borderRadius: 6, border: '1px solid #2952CC', background: '#EAF0FF' },
  btn: { padding: '13px 32px', background: '#2952CC', color: 'white', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer', width: '100%', marginTop: 8 },
  btnDisabled: { padding: '13px 32px', background: '#BDC3C7', color: 'white', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'not-allowed', width: '100%', marginTop: 8 },
};

const OptimizePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState<any>(null);
  const [companies, setCompanies] = useState<string[]>([]);
  const [regions, setRegions] = useState<any[]>([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [singleStore, setSingleStore] = useState('');
  const [result, setResult] = useState<OptimizeResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'setup' | 'results'>('setup');

  useEffect(() => {
    Promise.all([
      api.get(`/lists/${id}`),
      api.get('/products/meta/companies'),
      api.get('/products/meta/regions'),
    ]).then(([listRes, compRes, regRes]) => {
      setList(listRes.data);
      const compData = Array.isArray(compRes.data) ? compRes.data : (compRes.data?.content || []);
      const names = compData.map((c: any) => c.name || c.companyName || c).filter(Boolean).sort();
      setCompanies(names);
      const regData = Array.isArray(regRes.data) ? regRes.data : (regRes.data?.content || []);
      setRegions(regData);
    });
  }, [id]);

  useEffect(() => {
    const url = selectedRegion
      ? `/products/meta/companies?city=${encodeURIComponent(selectedRegion)}`
      : '/products/meta/companies';
    api.get(url).then(res => {
      const data = Array.isArray(res.data) ? res.data : (res.data?.content || []);
      const names = data.map((c: any) => c.name || c.companyName || c).filter(Boolean).sort();
      setCompanies(names);
      setSelectedCompanies([]);
    });
  }, [selectedRegion]);

  const toggleCompany = (name: string) =>
    setSelectedCompanies(prev => prev.includes(name) ? prev.filter(c => c !== name) : [...prev, name]);
  const selectAll = () => setSelectedCompanies([...companies]);
  const clearAll = () => setSelectedCompanies([]);

  const runOptimize = async () => {
    if (selectedCompanies.length === 0) return alert('Επίλεξε τουλάχιστον ένα κατάστημα.');
    
    setLoading(true);
    try {
      const res = await api.post(`/lists/${id}/optimize`, {
        selectedCompanies,
        singleStoreCompany: singleStore || null,
      });
      setResult(res.data);
      setStep('results');
    } catch {
      alert('Σφάλμα κατά τη σύγκριση. Δοκίμασε ξανά.');
    } finally {
      setLoading(false);
    }
  };

  if (!list) return <div style={{ textAlign: 'center', marginTop: 80 }}>Φόρτωση...</div>;

  // ── Results view ──
  if (step === 'results' && result) {
    return (
      <OptimizeResults
        data={result}
        groupStores={selectedCompanies}
        compareStore={singleStore}
        onBack={() => setStep('setup')}
      />
    );
  }

  // ── Setup view ──
  return (
    <div style={S.page}>
      <button onClick={() => navigate('/lists')} style={S.back}>← Πίσω στις λίστες</button>
      <h1 style={S.h1}>⚙️ Σύγκριση Τιμών Λίστας</h1>
      <p style={S.sub}>Λίστα: <strong>{list.name}</strong> — {list.items?.length || 0} προϊόντα</p>

      {/* Products preview */}
      <div style={S.card}>
        <div style={S.cardTitle}>📦 Προϊόντα λίστας</div>
        {(list.items || []).map((item: any) => (
          <div key={item.id} style={{ fontSize: 13, padding: '4px 0', borderBottom: '1px solid #F0F4F8', color: '#2952CC' }}>
            • {item.product_name} ×{item.quantity}
          </div>
        ))}
      </div>

      {/* α. Select stores */}
      <div style={S.card}>
        <div style={S.cardTitle}>α. Σε ποια καταστήματα να ψάξουμε;</div>
        {regions.length > 0 && (
          <>
            <label style={S.label}>Φίλτρο ανά Πόλη/Περιοχή (προαιρετικό)</label>
            <select style={S.select} value={selectedRegion} onChange={e => setSelectedRegion(e.target.value)}>
              <option value="">Όλες οι περιοχές</option>
              {regions.map((r: any, i: number) => (
                <option key={i} value={r.name || r}>{r.name || r}</option>
              ))}
            </select>
          </>
        )}
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <button onClick={selectAll} style={{ fontSize: 12, padding: '4px 10px', background: '#EAF0FF', border: '1px solid #C5D5FF', borderRadius: 5, cursor: 'pointer', color: '#2952CC' }}>
            Επιλογή Όλων
          </button>
          <button onClick={clearAll} style={{ fontSize: 12, padding: '4px 10px', background: '#FEF9F9', border: '1px solid #FADBD8', borderRadius: 5, cursor: 'pointer', color: '#E74C3C' }}>
            Καθαρισμός
          </button>
          <span style={{ fontSize: 12, color: '#888', alignSelf: 'center' }}>{selectedCompanies.length} επιλεγμένα</span>
        </div>
        <div style={S.checkGrid}>
          {companies.map(name => (
            <label key={name} style={selectedCompanies.includes(name) ? S.checkItemOn : S.checkItem}>
              <input type="checkbox" checked={selectedCompanies.includes(name)} onChange={() => toggleCompany(name)} style={{ cursor: 'pointer' }} />
              <span>{name}</span>
            </label>
          ))}
        </div>
      </div>

      {/* β. Single store */}
      <div style={S.card}>
        <div style={S.cardTitle}>β. Σύγκριση: αν αγόραζες τα πάντα από ένα κατάστημα</div>
        <label style={S.label}>Επίλεξε κατάστημα για σύγκριση κόστους (Χ)</label>
        <select style={S.select} value={singleStore} onChange={e => setSingleStore(e.target.value)}>
          <option value="">-- Επίλεξε κατάστημα --</option>
          {companies.map(name => <option key={name} value={name}>{name}</option>)}
        </select>
      </div>

      <button
        onClick={runOptimize}
        disabled={loading || selectedCompanies.length === 0}
        style={loading || selectedCompanies.length === 0 ? S.btnDisabled : S.btn}
      >
        {loading ? '⏳ Υπολογισμός...' : '💡 Βρες τις καλύτερες τιμές'}
      </button>
    </div>
  );
};

export default OptimizePage;
